// src/features/tidsregistering/pages/AnalysePage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Skeleton, Alert, Button, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { Analytics as AnalyticsIcon } from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useTidsregistreringStatistics } from '../hooks/useTidsregistreringStatistics';
import { useStaff } from '../../staff/hooks/useStaff';
import { tokens } from '../../../theme';
import { statCardGradientSx } from '../../../theme/statCardStyle';

import PageHeader from '../../../components/common/PageHeader';

const CATEGORIES = {
  attendance: { label: 'Fremmøde', colorKey: 'blueAccent' },
  sickleave: { label: 'Sygdom', colorKey: 'redAccent' },
  personal: { label: 'Personligt', colorKey: 'yellowAccent' },
  vacation: { label: 'Ferie', colorKey: 'greenAccent' },
  leave: { label: 'Orlov', colorKey: 'pinkAccent' },
};

function isValidDate(d) {
  return d instanceof Date && !isNaN(d.getTime());
}

function StatCard({ title, value, subtitle, color, loading }) {
  return (
    <Card
      sx={{
        height: '100%',
        minWidth: 0,
        ...statCardGradientSx(color),
      }}
    >
      <CardContent>
        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <Typography variant="h4" fontWeight="bold" sx={{ color }} noWrap title={String(value)}>
            {value}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight="medium"
          noWrap
          title={title}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" noWrap title={subtitle}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function HoursTrendChart({ title, points, color, loading }) {
  return (
    <Card sx={{ height: 400, minWidth: 0 }}>
      <CardContent sx={{ height: '100%' }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={300} />
        ) : points.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={points} margin={{ top: 5, right: 30, left: 20, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period_label" fontSize={12} angle={-30} textAnchor="end" height={50} />
              <YAxis fontSize={12} />
              <Tooltip
                formatter={(value, name) => [`${Number(value).toFixed(1)} t.`, name === 'avg_hours_per_staff' ? 'Gns. timer/medarbejder' : name]}
              />
              <Bar dataKey="avg_hours_per_staff" name="Gns. timer/medarbejder" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography variant="body2" color="text.secondary">Ingen data i perioden</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalysePage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { statistics, loading, error, fetchAnalyse } = useTidsregistreringStatistics();
  const { staff, loading: staffLoading, fetchDepartmentStaff } = useStaff();

  const [startDate, setStartDate] = useState(subDays(new Date(), 90));
  const [endDate, setEndDate] = useState(new Date());

  const staffMap = useMemo(
    () => Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s.name])),
    [staff]
  );

  const runFetch = useCallback(() => {
    if (!isValidDate(startDate) || !isValidDate(endDate) || startDate > endDate) return;
    fetchAnalyse(format(startDate, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'));
  }, [fetchAnalyse, startDate, endDate]);

  useEffect(() => {
    fetchDepartmentStaff();
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryColor = (cat) =>
    CATEGORIES[cat] ? colors[CATEGORIES[cat].colorKey][500] : colors.grey[500];

  const breakdownData = useMemo(
    () => (statistics?.absence_breakdown ?? []).map((entry) => ({
      ...entry,
      label: CATEGORIES[entry.statistical_category]?.label ?? entry.statistical_category,
    })),
    [statistics]
  );

  const topSygedageData = useMemo(
    () => (statistics?.top_sygedage ?? []).map((entry) => ({
      ...entry,
      name: staffMap[entry.staff_member_id] ?? entry.staff_member_id,
    })),
    [statistics, staffMap]
  );

  const topEntry = topSygedageData[0];

  // Weighted by staff_count per period so headcount-light periods don't skew the average.
  const weightedAvg = (points) => {
    if (!points?.length) return null;
    const totalStaffPeriods = points.reduce((sum, p) => sum + Number(p.staff_count), 0);
    if (!totalStaffPeriods) return null;
    const totalHours = points.reduce((sum, p) => sum + Number(p.avg_hours_per_staff) * Number(p.staff_count), 0);
    return totalHours / totalStaffPeriods;
  };
  const avgWeekly = useMemo(() => weightedAvg(statistics?.weekly_hours), [statistics]);
  const avgMonthly = useMemo(() => weightedAvg(statistics?.monthly_hours), [statistics]);
  const totalAbsenceHours = useMemo(
    () => breakdownData.reduce((sum, e) => sum + Number(e.total_hours), 0),
    [breakdownData]
  );

  // Union of both sources, not either one alone: every row the API returned is
  // kept (someone deactivated mid-period still has hours but is missing from
  // the active staff list), and staff with zero activity are padded in as
  // zeros (the API omits staff with no worked/absence hours).
  const perEmployeeData = useMemo(() => {
    if (!statistics) return [];
    const entries = statistics.per_employee ?? [];
    const seen = new Set(entries.map((e) => e.staff_member_id));
    const padded = (staff ?? [])
      .filter((s) => !seen.has(s.staff_member_id))
      .map((s) => ({
        staff_member_id: s.staff_member_id,
        worked_hours: 0,
        days_worked: 0,
        absence_hours_by_category: {},
        total_absence_hours: 0,
      }));
    return [...entries, ...padded].sort(
      (a, b) =>
        Number(b.worked_hours) - Number(a.worked_hours) ||
        a.staff_member_id.localeCompare(b.staff_member_id)
    );
  }, [statistics, staff]);
  const presentCategories = useMemo(
    () => Object.keys(CATEGORIES).filter((cat) =>
      perEmployeeData.some((e) => e.absence_hours_by_category?.[cat] !== undefined)
    ),
    [perEmployeeData]
  );

  // `staff` starts as [] and stays truthy, so it can't gate the staff fetch --
  // OR the loading flags and gate on the data that is actually absent until
  // both land, otherwise the table flashes raw UUIDs before names arrive.
  const isInitialLoading = (loading || staffLoading) && !statistics;
  const isDateRangeInvalid =
    !isValidDate(startDate) || !isValidDate(endDate) || startDate > endDate;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={AnalyticsIcon}
        title={t('common:navigation.tidsregistering_analyse')}
        subtitle={t('tidsregistering:page_subtitles.analyse')}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <>
          <DatePicker
            label="Fra dato"
            value={startDate}
            onChange={setStartDate}
            slotProps={{ textField: { size: 'small', error: !isValidDate(startDate) } }}
          />
          <DatePicker
            label="Til dato"
            value={endDate}
            onChange={setEndDate}
            slotProps={{ textField: { size: 'small', error: !isValidDate(endDate) } }}
          />
        </>
        <Button variant="contained" onClick={runFetch} disabled={loading || isDateRangeInvalid}>
          Anvend
        </Button>
      </Box>

      {isDateRangeInvalid && !loading && (
        <Alert severity="warning">"Fra dato" skal være før eller lig med "Til dato".</Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <StatCard
          title="Højeste sygedage (periode)"
          value={topEntry ? `${topEntry.name}` : '—'}
          subtitle={topEntry ? `${Number(topEntry.total_hours).toFixed(1)} timer` : undefined}
          color={colors.redAccent[500]}
          loading={isInitialLoading}
        />
        <StatCard
          title="Gns. timer/uge"
          value={avgWeekly !== null ? `${avgWeekly.toFixed(1)} t.` : '—'}
          color={colors.blueAccent[500]}
          loading={isInitialLoading}
        />
        <StatCard
          title="Gns. timer/måned"
          value={avgMonthly !== null ? `${avgMonthly.toFixed(1)} t.` : '—'}
          color={colors.blueAccent[500]}
          loading={isInitialLoading}
        />
        <StatCard
          title="Total fraværstimer"
          value={`${totalAbsenceHours.toFixed(1)} t.`}
          color={colors.yellowAccent[500]}
          loading={isInitialLoading}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
        }}
      >
        <HoursTrendChart title="Timer pr. uge (gns. pr. medarbejder)" points={statistics?.weekly_hours ?? []} color={colors.blueAccent[500]} loading={isInitialLoading} />
        <HoursTrendChart title="Timer pr. måned (gns. pr. medarbejder)" points={statistics?.monthly_hours ?? []} color={colors.blueAccent[400]} loading={isInitialLoading} />

        <Card sx={{ height: 400, minWidth: 0 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" gutterBottom>Fravær fordelt på type</Typography>
            {isInitialLoading ? (
              <Skeleton variant="rectangular" width="100%" height={300} />
            ) : breakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={breakdownData}
                    cx="50%"
                    cy="50%"
                    outerRadius="70%"
                    dataKey="total_hours"
                    nameKey="label"
                    label={({ label, total_hours }) => `${label} (${Number(total_hours).toFixed(0)}t)`}
                    labelLine={false}
                  >
                    {breakdownData.map((entry) => (
                      <Cell key={entry.statistical_category} fill={categoryColor(entry.statistical_category)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${Number(value).toFixed(1)} timer`, 'Timer']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height={300}>
                <Typography variant="body2" color="text.secondary">Ingen data i perioden</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ height: 400, minWidth: 0 }}>
          <CardContent sx={{ height: '100%' }}>
            <Typography variant="h6" gutterBottom>Top sygedage pr. medarbejder</Typography>
            {isInitialLoading ? (
              <Skeleton variant="rectangular" width="100%" height={300} />
            ) : topSygedageData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topSygedageData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={12} domain={[0, (max) => Math.max(max * 1.15, 1)]} />
                  <YAxis type="category" dataKey="name" fontSize={12} width={100} />
                  <Tooltip formatter={(value) => [`${Number(value).toFixed(1)} timer`, 'Timer']} />
                  <Bar dataKey="total_hours" name="Timer" fill={colors.redAccent[500]} radius={[0, 4, 4, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height={300}>
                <Typography variant="body2" color="text.secondary">Ingen data i perioden</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ minWidth: 0 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Medarbejderoversigt</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Medarbejder</TableCell>
                  <TableCell align="right">Timer arbejdet</TableCell>
                  <TableCell align="right">Dage arbejdet</TableCell>
                  {presentCategories.map((cat) => (
                    <TableCell key={cat} align="right">{CATEGORIES[cat]?.label ?? cat}</TableCell>
                  ))}
                  <TableCell align="right">Total fravær</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isInitialLoading ? (
                  <TableRow>
                    <TableCell colSpan={4 + Object.keys(CATEGORIES).length} sx={{ py: 2 }}>
                      <Skeleton variant="rectangular" width="100%" height={120} />
                    </TableCell>
                  </TableRow>
                ) : perEmployeeData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4 + presentCategories.length} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                      Ingen data i perioden
                    </TableCell>
                  </TableRow>
                ) : perEmployeeData.map((entry) => (
                  <TableRow key={entry.staff_member_id} hover>
                    <TableCell>{staffMap[entry.staff_member_id] ?? entry.staff_member_id}</TableCell>
                    <TableCell align="right">{Number(entry.worked_hours).toFixed(1)} t.</TableCell>
                    <TableCell align="right">{entry.days_worked}</TableCell>
                    {presentCategories.map((cat) => (
                      <TableCell key={cat} align="right">
                        {entry.absence_hours_by_category?.[cat] !== undefined
                          ? `${Number(entry.absence_hours_by_category[cat]).toFixed(1)} t.`
                          : '—'}
                      </TableCell>
                    ))}
                    <TableCell align="right">{Number(entry.total_absence_hours).toFixed(1)} t.</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
